import React, { useState, useEffect } from 'react';
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  Navigate 
} from 'react-router-dom';
import { 
  ChevronRight, 
  CheckCircle2, 
  TrendingUp, 
  Globe, 
  Zap, 
  Users, 
  BarChart3, 
  Layers, 
  Mail, 
  Phone, 
  MapPin, 
  Menu, 
  X,
  ChevronDown,
  Award, 
  BookOpen, 
  Settings, 
  ShieldCheck,
  Calendar,
  Network, 
  Rocket,
  XCircle,
  FileQuestion,
  TrendingDown,
  Layout,
  Trophy,
  Building2,
  Clock,
  ArrowRight,
  Quote,
  Search,
  PenTool,
  Send,
  PartyPopper,
  Target,
  Plus,
  Minus,
  MessageCircle,
  Lock,
  ArrowLeft,
  FileText,
  MousePointer2,
  ExternalLink,
  PieChart,
  Lightbulb,
  Info,
  Briefcase
} from 'lucide-react';

// Supabase
import { supabase } from './src/integrations/supabase/client';
import { AuthProvider } from './src/contexts/AuthContext';
import { NotificationProvider } from './src/contexts/NotificationContext';

// Components
import Marquee from './src/components/Marquee';
import VideoCarousel from './src/components/VideoCarousel';

// Admin Pages
import AdminLayout from './src/pages/Admin/AdminLayout';
import AdminDashboard from './src/pages/Admin/Dashboard';
import Clients from './src/pages/Admin/Clients';
import Pipeline from './src/pages/Admin/Pipeline';
import Workshops from './src/pages/Admin/Workshops';
import Resources from './src/pages/Admin/Resources';
import Automations from './src/pages/Admin/Automations';
import Login from './src/pages/Login';

// --- UI COMPONENTS (M3 Style) ---
const Button = ({ 
  children, 
  variant = 'filled', 
  className = '', 
  ...props 
}: { 
  children: React.ReactNode, 
  variant?: 'filled' | 'outlined' | 'text', 
  className?: string 
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  const baseStyles = "px-6 py-3 rounded-full font-semibold transition-all duration-200 flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50";
  const variants = {
    filled: "bg-primary text-white hover:bg-blue-700 shadow-sm",
    outlined: "border border-gray-300 text-primary hover:bg-blue-50",
    text: "text-primary hover:bg-blue-50 px-4"
  };

  return (
    <button className={`${baseStyles} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

const Card = ({ children, className = "", ...props }: { children: React.ReactNode, className?: string, [key: string]: any }) => (
  <div className={`bg-white border border-gray-200 rounded-4xl p-8 transition-all hover:shadow-md ${className}`} {...props}>
    {children}
  </div>
);

const Modal = ({ isOpen, onClose, title, children }: { isOpen: boolean, onClose: () => void, title: string, children?: React.ReactNode }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-4xl w-full max-w-xl shadow-2xl overflow-hidden animate-in zoom-in duration-300">
        <div className="p-8 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-2xl font-bold text-charcoal">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X size={24} className="text-charcoal-muted" />
          </button>
        </div>
        <div className="p-8 max-h-[70vh] overflow-y-auto">
          {children}
        </div>
        <div className="p-8 border-t border-gray-100 flex justify-end">
          <Button onClick={onClose}>Cerrar</Button>
        </div>
      </div>
    </div>
  );
};

// --- NAVIGATION ---
type ViewType = 'home' | 'grants' | 'seo' | 'membresias' | 'ia' | 'calendario' | 'guia' | 'reportes' | 'blog' | 'terminos' | 'privacidad' | 'cookies';

const Header = ({ currentView, setView }: { currentView: ViewType, setView: (v: ViewType) => void }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Servicios', href: '#servicios' },
    { name: 'Fondos', href: '#fondos' },
    { name: 'Casos', href: '#casos' },
    { name: 'Proceso', href: '#proceso' },
    { name: 'Precios', href: '#precios' },
  ];

  const handleLinkClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    
    if (currentView !== 'home') {
      setView('home');
      setTimeout(() => {
        const id = href.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    } else {
      const id = href.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <>
      <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled || currentView !== 'home' ? 'bg-white border-b border-gray-200 py-3' : 'bg-white/80 backdrop-blur-md py-5'}`}>
        <div className="container mx-auto px-6 max-w-7xl flex items-center justify-between">
          <button onClick={() => { setView('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-2 group">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center font-bold text-white text-xl shadow-sm group-hover:scale-105 transition-transform">M</div>
            <span className="font-bold text-xl tracking-tight text-charcoal">
              MediaNews <span className="text-primary">Tech Lab</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <a 
                key={link.name} 
                href={link.href} 
                onClick={(e) => handleLinkClick(e, link.href)}
                className="text-sm font-medium text-charcoal-muted hover:text-primary transition-colors"
              >
                {link.name}
              </a>
            ))}
            <Button onClick={(e: any) => handleLinkClick(e, '#contacto')}>
              Consultoría Gratis
            </Button>
          </nav>

          <button 
            onClick={() => setMobileMenuOpen(true)} 
            className="lg:hidden p-2 text-charcoal hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Abrir menú"
          >
            <Menu size={28} />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div 
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-[60] transition-opacity duration-300 lg:hidden ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      <div 
        className={`fixed top-0 right-0 h-full w-[85%] max-sm:w-full bg-white z-[70] shadow-2xl transition-transform duration-500 ease-in-out lg:hidden transform ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex flex-col h-full p-8">
          <div className="flex items-center justify-between mb-12">
            <button onClick={() => { setView('home'); setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center font-bold text-white text-lg">M</div>
              <span className="font-bold text-lg text-charcoal">MediaNews</span>
            </button>
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 text-charcoal-muted hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Cerrar menú"
            >
              <X size={24} />
            </button>
          </div>

          <nav className="flex flex-col gap-4 mb-12">
            {navLinks.map((link) => (
              <a 
                key={link.name} 
                href={link.href} 
                onClick={(e) => handleLinkClick(e, link.href)} 
                className="text-lg font-semibold text-charcoal hover:text-primary py-2 transition-all flex items-center justify-between"
              >
                {link.name}
                <ChevronRight size={18} className="text-gray-300" />
              </a>
            ))}
          </nav>

          <div className="mt-auto">
            <Button className="w-full py-4" onClick={(e: any) => handleLinkClick(e, '#contacto')}>
              Empezar Ahora
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

// --- HOME COMPONENTS ---
const Hero = () => (
  <section className="relative pt-40 pb-24 overflow-hidden bg-white">
    <div className="container mx-auto px-6 max-w-7xl relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-primary text-xs font-bold mb-8 uppercase tracking-wider">
          <Award size={14} /> Estrategia • Innovación • Resultados
        </div>
        <h1 className="text-5xl lg:text-7xl font-extrabold text-charcoal leading-[1.1] mb-8 tracking-tight">
          Accede a <span className="text-primary">US$200K+</span> en Grants para Tu Medio Dominicano
        </h1>
        <p className="text-xl lg:text-2xl text-charcoal-muted mb-12 max-w-3xl mx-auto leading-relaxed">
          Somos la única consultora especializada en <span className="text-charcoal font-semibold">Google News Initiative</span>, Meta y SIP para medios RD. Consultoría estratégica para captar fondos, optimizar tecnología y escalar audiencia.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button className="text-lg px-10 py-4" onClick={() => document.getElementById('servicios')?.scrollIntoView({ behavior: 'smooth' })}>
            Ver Nuestros Servicios <ChevronRight size={20} />
          </Button>
          <Button variant="outlined" className="text-lg px-10 py-4" onClick={() => document.getElementById('casos')?.scrollIntoView({ behavior: 'smooth' })}>
            Conoce Casos de Éxito
          </Button>
        </div>
      </div>
    </div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-50/50 rounded-full blur-3xl -z-10"></div>
  </section>
);

const ProblemSolution = () => (
  <section className="py-32 bg-surface-variant">
    <div className="container mx-auto px-6 max-w-7xl">
      <div className="grid lg:grid-cols-2 gap-24 items-center">
        <div>
          <span className="text-primary font-bold uppercase tracking-widest text-xs mb-4 block">Barreras Actuales</span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-charcoal mb-8 leading-tight">
            ¿Por Qué Tu Medio No Accede a Fondos Internacionales?
          </h2>
          <div className="space-y-8">
            {[
              { text: "Aplicaciones rechazadas por falta de preparación técnica", icon: <XCircle className="text-red-500" /> },
              { text: "<10% de medios RD han accedido a GNI", icon: <TrendingDown className="text-red-500" /> },
              { text: "Convocatorias escritas en inglés con requisitos complejos", icon: <Globe className="text-primary" /> },
              { text: "Infraestructura digital no cumple estándares GNI", icon: <Layout className="text-primary" /> }
            ].map((item, i) => (
              <div key={i} className="flex gap-5 items-start">
                <div className="mt-1 p-2 bg-white rounded-xl shadow-sm">{item.icon}</div>
                <p className="text-charcoal-muted text-lg font-medium leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
        
        <Card className="border-none shadow-xl p-12">
          <div className="flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-blue-50 rounded-3xl flex items-center justify-center text-primary mb-8">
              <CheckCircle2 size={40} />
            </div>
            <span className="text-primary font-bold uppercase tracking-widest text-xs mb-4 block">Nuestra Solución</span>
            <h3 className="text-3xl font-bold text-charcoal mb-6">Nosotros Cerramos Esa Brecha</h3>
            <ul className="text-left space-y-4 mb-8">
              {[
                "Diseñamos tu proyecto con teoría de cambio clara",
                "Instalamos infraestructura técnica que exigen los fondos",
                "Escribimos tu aplicación en inglés profesional",
                "Aumentamos tus probabilidades de aprobación en +300%"
              ].map((item, i) => (
                <li key={i} className="flex gap-3 items-center text-charcoal-muted font-medium">
                  <CheckCircle2 size={18} className="text-primary shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <Button 
              className="w-full"
              onClick={() => {
                const element = document.getElementById('proceso');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Saber Más
            </Button>
          </div>
        </Card>
      </div>
    </div>
  </section>
);

const Services = () => {
  const [selectedService, setSelectedService] = useState<any>(null);
  const services = [
    { 
      title: "Grant Consultation", 
      category: "Estrategia",
      description: "Auditoría + diseño de proyecto + aplicación en inglés + acompañamiento estratégico personalizado.", 
      details: "Nuestro equipo se encarga de todo el ciclo de vida del grant. Desde la identificación de oportunidades alineadas con su estrategia editorial hasta la redacción técnica en inglés profesional.",
      icon: <Briefcase size={32} />,
      price: "Desde US$1,500 + success fee" 
    },
    { 
      title: "Technical Setup", 
      category: "Tecnología",
      description: "Google News + Analytics 4 + SEO + Newsletter. Cumple requisitos técnicos de Google News Initiative.", 
      details: "Muchos grants son rechazados por fallos técnicos básicos. Implementamos una infraestructura robusta que incluye optimización de Core Web Vitals y GA4.",
      icon: <Settings size={32} />,
      price: "Desde US$1,500" 
    },
    { 
      title: "Monthly Retainer", 
      category: "Crecimiento",
      description: "Acompañamiento estratégico continuo. Dashboards + reportes + alertas de nuevas convocatorias globales.", 
      details: "Un servicio llave en mano para medios que buscan una transformación digital sostenida.",
      icon: <TrendingUp size={32} />,
      price: "US$600-1,200/mes" 
    },
    { 
      title: "Cohort Program", 
      category: "Educación",
      description: "Programa para periodistas independientes. 3 meses intensivo + aplicación grupal a fondos de innovación.", 
      details: "Diseñado para periodistas que quieren lanzar su propio medio o startup.",
      icon: <Users size={32} />,
      price: "US$400/persona" 
    }
  ];
  return (
    <section id="servicios" className="py-32 bg-white scroll-mt-24">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-20">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-charcoal mb-6">Nuestros Servicios</h2>
          <p className="text-charcoal-muted max-w-2xl mx-auto text-xl">Paquetes diseñados para cada tipo de medio</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((s, i) => (
            <Card key={i} className="flex flex-col h-full">
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-primary mb-6">
                {s.icon}
              </div>
              <span className="text-[10px] font-bold text-primary uppercase tracking-widest mb-2">{s.category}</span>
              <h3 className="text-2xl font-bold text-charcoal mb-4">{s.title}</h3>
              <p className="text-charcoal-muted mb-8 text-lg leading-relaxed flex-grow">{s.description}</p>
              <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                <div className="text-charcoal font-bold text-xl">{s.price}</div>
                <Button variant="outlined" onClick={() => setSelectedService(s)}>
                  Ver Detalles
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
      <Modal isOpen={!!selectedService} onClose={() => setSelectedService(null)} title={selectedService?.title || ''}>
        <div className="space-y-6">
          <p className="text-charcoal-muted text-lg leading-relaxed">{selectedService?.details}</p>
          <div className="bg-blue-50 p-6 rounded-3xl border border-blue-100">
            <p className="text-primary font-bold text-2xl">{selectedService?.price}</p>
          </div>
        </div>
      </Modal>
    </section>
  );
};

const AvailableFunds = ({ onApplyNow }: { onApplyNow: () => void }) => {
  const [selectedFund, setSelectedFund] = useState<any>(null);
  const funds = [
    { 
      name: "Google News Initiative", 
      amount: "US$5K - US$350K", 
      frequency: "Anual/Bianual", 
      org: "Google Innovation Challenge", 
      details: "El fondo de innovación más prestigioso del mundo. Financia proyectos de IA, suscripciones y lucha contra la desinformación.",
      website: "https://newsinitiative.withgoogle.com/"
    },
    { 
      name: "Meta Journalism Project", 
      amount: "US$10K - US$50K", 
      frequency: "Anual", 
      org: "Meta Engagement Grant", 
      details: "Enfocado en aumentar el engagement de las audiencias y mejorar la distribución de noticias locales en redes sociales.",
      website: "https://www.facebook.com/journalismproject"
    },
    { 
      name: "News Equity Fund", 
      amount: "US$5K - US$25K", 
      frequency: "Rolling", 
      org: "International News Equity", 
      details: "Fondo dedicado a medios que sirven a comunidades subrepresentadas o minorías, buscando mayor diversidad en el ecosistema.",
      website: "https://newsinitiative.withgoogle.com/equity-fund/"
    },
    { 
      name: "SIP Labs", 
      amount: "US$10K - US$30K", 
      frequency: "Anual", 
      org: "Sociedad Interamericana de Prensa", 
      details: "Programas de aceleración digital para medios latinoamericanos con foco en sostenibilidad financiera.",
      website: "https://www.sipiapa.org/"
    }
  ];

  return (
    <section id="fondos" className="py-32 bg-surface-variant scroll-mt-24">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="max-w-3xl mb-20">
          <span className="text-primary font-bold uppercase tracking-widest text-xs mb-4 block">Oportunidades 2026</span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-charcoal mb-6">US$200K+ en Fondos Disponibles</h2>
          <p className="text-charcoal-muted text-xl leading-relaxed">Estas son algunas de las convocatorias a las que podemos ayudarte a aplicar:</p>
        </div>
        <div className="grid gap-6">
          {funds.map((f, i) => (
            <div key={i} className="bg-white p-8 rounded-4xl border border-gray-200 flex flex-col lg:flex-row items-center justify-between gap-8 hover:shadow-md transition-all">
              <div className="flex items-center gap-6 w-full lg:w-auto">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-primary shrink-0">
                  <Building2 size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-charcoal">{f.name}</h3>
                  <p className="text-charcoal-muted font-medium">{f.org}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between lg:justify-end gap-8 w-full lg:w-auto">
                <div className="text-left lg:text-right">
                  <p className="text-[10px] text-charcoal-muted uppercase tracking-widest font-bold mb-1">Monto Estimado</p>
                  <p className="text-2xl font-extrabold text-primary">{f.amount}</p>
                </div>
                <Button variant="outlined" onClick={() => setSelectedFund(f)}>Ver Detalles</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <Modal isOpen={!!selectedFund} onClose={() => setSelectedFund(null)} title={selectedFund?.name || ''}>
        <div className="space-y-8">
          <p className="text-charcoal-muted text-lg leading-relaxed">{selectedFund?.details}</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-6 rounded-3xl border border-gray-100">
              <p className="text-[10px] text-charcoal-muted uppercase font-bold tracking-widest mb-1">Monto</p>
              <p className="text-charcoal font-bold text-lg">{selectedFund?.amount}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-3xl border border-gray-100">
              <p className="text-[10px] text-charcoal-muted uppercase font-bold tracking-widest mb-1">Frecuencia</p>
              <p className="text-charcoal font-bold text-lg">{selectedFund?.frequency}</p>
            </div>
          </div>
          {selectedFund?.website && (
            <a href={selectedFund.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary font-bold hover:underline">
              <Globe size={18} /> Visitar Sitio Oficial
            </a>
          )}
          <Button className="w-full py-4" onClick={() => { setSelectedFund(null); onApplyNow(); }}>
            Iniciar Postulación <ArrowRight size={20} />
          </Button>
        </div>
      </Modal>
    </section>
  );
};

const CaseStudies = ({ onAction }: { onAction: () => void }) => {
  const [selectedCase, setSelectedCase] = useState<any>(null);
  const cases = [
    { 
      media: "Listín Diario",
      image: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&q=80&w=800",
      description: "El diario más antiguo de RD optimizó su presencia en Google Discover mediante nuestra consultoría técnica.",
      challenge: "Bajo tráfico orgánico desde dispositivos móviles y tiempos de carga superiores a 5 segundos.",
      solution: "Implementación de GNI Digital Growth Programme enfocado en Core Web Vitals y marcado de datos estructurados NewsArticle.",
      impact: ["Aumento del 40% en tráfico desde Google Discover", "Reducción del tiempo de carga a 1.2 segundos", "Aprobación de fondos para laboratorio de IA editorial"],
      stats: { metric: "+40%", label: "Tráfico Discover" }
    },
    { 
      media: "Diario Libre",
      image: "https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&q=80&w=800",
      description: "Líder en circulación gratuita, logramos implementar su primer modelo de suscripción digital con apoyo de GNI.",
      challenge: "Dependencia exclusiva de la publicidad programática con CPMs decrecientes.",
      solution: "Consultoría estratégica para el diseño de un 'Metered Paywall' y aplicación al GNI Innovation Challenge.",
      impact: ["Lanzamiento exitoso del modelo de suscripción digital", "Capacitación de 30+ periodistas en analítica orientada a conversión", "Obtención de Grant para infraestructura de datos"],
      stats: { metric: "+15K", label: "Nuevos Suscriptores" }
    },
    { 
      media: "El Nuevo Diario",
      image: "https://images.unsplash.com/photo-1588681664899-f142ff2dc9b1?auto=format&fit=crop&q=80&w=800",
      description: "Transformación de redacción tradicional a ecosistema digital multicanal.",
      challenge: "Infraestructura técnica obsoleta que impedía la escalabilidad de su oferta de video.",
      solution: "Rediseño de arquitectura de nube y optimización SEO para video nativo.",
      impact: ["Aumento del 120% en reproducciones de video digital", "Migración exitosa a CMS de alto rendimiento", "Participación en programas de Meta Journalism Project"],
      stats: { metric: "+120%", label: "Video Views" }
    }
  ];

  return (
    <section id="casos" className="py-32 bg-white scroll-mt-24">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-charcoal mb-6">Casos de Éxito en la Industria</h2>
          <p className="text-charcoal-muted text-xl">Resultados reales obtenidos a través de innovación y fondos estratégicos.</p>
        </div>
        <div className="grid lg:grid-cols-3 gap-8">
          {cases.map((c, i) => (
            <Card key={i} className="flex flex-col p-0 overflow-hidden">
              <div className="relative h-56 overflow-hidden">
                <img src={c.image} className="w-full h-full object-cover" alt={c.media} />
                <div className="absolute top-4 right-4 bg-primary text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg">
                  {c.stats.metric}
                </div>
              </div>
              <div className="p-8 flex flex-col flex-grow">
                <span className="text-[10px] font-bold text-primary uppercase tracking-widest mb-2">Caso de Estudio</span>
                <h3 className="text-2xl font-bold text-charcoal mb-4">{c.media}</h3>
                <p className="text-charcoal-muted text-base mb-8 line-clamp-3 flex-grow">{c.description}</p>
                <Button variant="outlined" className="w-full" onClick={() => setSelectedCase(c)}>
                  Ver Impacto <ChevronRight size={18} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
      <Modal isOpen={!!selectedCase} onClose={() => setSelectedCase(null)} title={`Impacto: ${selectedCase?.media}`}>
        <div className="space-y-8">
          <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 flex items-start gap-3">
            <Info size={20} className="text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-charcoal-muted font-medium italic">Este caso se presenta como un ejemplo de éxito en la industria bajo programas globales de GNI.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 p-6 rounded-3xl border border-blue-100 text-center">
              <p className="text-primary font-extrabold text-3xl mb-1">{selectedCase?.stats.metric}</p>
              <p className="text-charcoal-muted text-[10px] font-bold uppercase tracking-widest">{selectedCase?.stats.label}</p>
            </div>
            <div className="bg-charcoal p-6 rounded-3xl text-center">
              <Trophy className="mx-auto text-yellow-400 mb-2" size={24} />
              <p className="text-white text-[10px] font-bold uppercase tracking-widest">Hito</p>
              <p className="text-blue-200 font-bold text-xs">Fondo de Innovación</p>
            </div>
          </div>
          <div className="space-y-6">
            <section>
              <h4 className="text-charcoal font-bold uppercase text-xs tracking-widest mb-3 flex items-center gap-2"><XCircle size={16} className="text-red-500" /> El Desafío</h4>
              <p className="text-charcoal-muted font-medium leading-relaxed">{selectedCase?.challenge}</p>
            </section>
            <section>
              <h4 className="text-charcoal font-bold uppercase text-xs tracking-widest mb-3 flex items-center gap-2"><Lightbulb size={16} className="text-primary" /> Solución</h4>
              <p className="text-charcoal-muted font-medium leading-relaxed">{selectedCase?.solution}</p>
            </section>
            <section>
              <h4 className="text-charcoal font-bold uppercase text-xs tracking-widest mb-3 flex items-center gap-2"><PieChart size={16} className="text-blue-500" /> Resultados Clave</h4>
              <ul className="space-y-3">
                {selectedCase?.impact.map((item: string, idx: number) => (
                  <li key={idx} className="flex gap-3 text-charcoal-muted font-medium text-sm">
                    <CheckCircle2 size={16} className="text-primary shrink-0 mt-0.5" /> {item}
                  </li>
                ))}
              </ul>
            </section>
          </div>
          <Button className="w-full py-4" onClick={() => { setSelectedCase(null); onAction(); }}>
            Quiero estos resultados <ArrowRight size={20} />
          </Button>
        </div>
      </Modal>
    </section>
  );
};

const Process = () => {
  const steps = [
    { step: "01", title: "Diagnóstico Rápido", time: "Semana 1", icon: <Search className="text-primary" size={24} />, items: ["Auditoría editorial, negocio y técnica", "Identificamos fondos con mejor fit"] },
    { step: "02", title: "Diseño de Proyecto", time: "Semanas 2-3", icon: <PenTool className="text-primary" size={24} />, items: ["Teoría de cambio clara", "Presupuesto detallado", "Casos de uso específicos"] },
    { step: "03", title: "Aplicación Profesional", time: "Semana 4", icon: <Send className="text-primary" size={24} />, items: ["Redacción en inglés", "Revisión múltiple", "Envío estratégico"] },
    { step: "04", title: "Acompañamiento", time: "Sostenido", icon: <PartyPopper className="text-primary" size={24} />, items: ["Follow-up con evaluadores", "Preparación para entrevistas", "Celebración cuando aprueban 🎉"] }
  ];
  return (
    <section id="proceso" className="py-32 bg-surface-variant scroll-mt-24">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-charcoal mb-6">Cómo Trabajamos Contigo</h2>
          <p className="text-charcoal-muted text-xl">Un roadmap estructurado para maximizar tus posibilidades de éxito.</p>
        </div>
        <div className="grid lg:grid-cols-4 gap-8">
          {steps.map((item, i) => (
            <Card key={i} className="relative border-none shadow-sm hover:shadow-md">
              <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-primary mb-6">{item.icon}</div>
              <div className="inline-block px-3 py-1 bg-blue-50 text-primary text-[10px] font-bold uppercase tracking-widest rounded-full mb-4">{item.time}</div>
              <h3 className="text-2xl font-bold text-charcoal mb-4">{item.title}</h3>
              <ul className="space-y-3">
                {item.items.map((bullet, j) => (
                  <li key={j} className="text-charcoal-muted text-sm font-medium flex gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 shrink-0"></div> {bullet}
                  </li>
                ))}
              </ul>
              <div className="absolute top-8 right-8 text-4xl font-black text-gray-100 -z-10">{item.step}</div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const Pricing = ({ onSelectPlan }: { onSelectPlan: (plan: string) => void }) => {
  const tiers = [
    { name: "Medios Grandes", target: "Listín, Hoy, El Caribe", price: "US$18,000-20,000", period: "/año", desc: "Grant Consultation + Retainer Pro", roi: "US$50K-150K", features: ["Consultoría Ilimitada", "Equipo de Devs Dedicado", "IA & Workflow Automation", "Relaciones Directas con Donantes"] },
    { name: "Medios Medianos", target: "Acento, elDinero, AlMomento", price: "US$9,000-11,000", period: "/año", desc: "Grant Consultation + Technical Setup", roi: "US$15K-50K", features: ["Búsqueda Mensual de Grants", "Redacción de 4 Aplicaciones/año", "Optimización Técnica SEO", "Manejo de Reportes"], recommended: true },
    { name: "Medios Regionales", target: "Diario 55, La Información", price: "US$4,500-5,500", period: " único", desc: "Grant Consultation + Workshop", roi: "US$5K-25K", features: ["Auditoría de SEO Inicial", "Workshop Intensivo (2 días)", "Template de Aplicación Ganador", "Consultoría Técnica (5h)"] }
  ];
  return (
    <section id="precios" className="py-32 bg-white scroll-mt-24">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-charcoal mb-6">Planes de Inversión</h2>
          <p className="text-charcoal-muted text-xl">Estrategias diseñadas para cada escala de operación periodística.</p>
        </div>
        <div className="grid lg:grid-cols-3 gap-8 items-stretch">
          {tiers.map((t, i) => (
            <Card key={i} className={`flex flex-col relative ${t.recommended ? 'border-primary ring-1 ring-primary shadow-xl' : ''}`}>
              {t.recommended && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">Más Popular</div>}
              <div className="mb-8">
                <div className="text-primary font-bold text-xs uppercase tracking-widest mb-2 flex items-center gap-2"><Target size={14} /> {t.target}</div>
                <h3 className="text-2xl font-bold text-charcoal mb-4">{t.name}</h3>
                <div className="text-3xl font-extrabold text-charcoal flex items-baseline gap-1">{t.price}<span className="text-sm text-charcoal-muted font-normal">{t.period}</span></div>
                <p className="text-charcoal-muted text-sm mt-3 font-medium">{t.desc}</p>
              </div>
              <div className="bg-blue-50 rounded-3xl p-6 mb-8 border border-blue-100">
                <div className="flex items-center gap-2 mb-1"><TrendingUp size={16} className="text-primary" /><span className="text-[10px] font-bold uppercase tracking-widest text-charcoal-muted">ROI esperado de Grants</span></div>
                <div className="text-2xl font-extrabold text-primary">{t.roi}</div>
              </div>
              <ul className="space-y-4 mb-10 flex-grow">
                {t.features.map((f, j) => (
                  <li key={j} className="flex gap-3 text-sm font-medium text-charcoal items-start"><CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              <Button variant={t.recommended ? 'filled' : 'outlined'} className="w-full py-4" onClick={() => onSelectPlan(t.name)}>Seleccionar Plan</Button>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const faqs = [
    { question: "¿Qué es un grant y cómo funciona?", answer: "Un grant es una donación no reembolsable otorgada por organizaciones como Google o Meta para proyectos de innovación, sostenibilidad o tecnología en medios de comunicación." },
    { question: "¿Mi medio es elegible para aplicar a GNI?", answer: "Sí, siempre que produzcas contenido original, tengas una operación establecida en República Dominicana y demuestres un plan claro de innovación." },
    { question: "¿Cuánto tiempo toma el proceso de aplicación?", answer: "Normalmente, el proceso de preparación con nosotros toma 4 semanas. Una vez enviado, el donante suele tardar de 2 a 4 meses en evaluar." },
    { question: "¿Qué pasa si mi aplicación es rechazada?", answer: "Analizamos detalladamente el feedback, ajustamos la propuesta y volvemos a postular en la siguiente ventana de oportunidad." }
  ];
  return (
    <section className="py-32 bg-surface-variant">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="text-center mb-20">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-charcoal mb-6">Preguntas Frecuentes</h2>
          <p className="text-charcoal-muted text-xl">Resolvemos tus dudas sobre el ecosistema de financiamiento global.</p>
        </div>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-white rounded-3xl border border-gray-200 overflow-hidden transition-all">
              <button onClick={() => setOpenIndex(openIndex === i ? -1 : i)} className="w-full flex justify-between items-center p-8 text-left">
                <span className={`text-lg font-bold ${openIndex === i ? 'text-primary' : 'text-charcoal'}`}>{faq.question}</span>
                <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${openIndex === i ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>{openIndex === i ? <Minus size={18} /> : <Plus size={18} />}</div>
              </button>
              {openIndex === i && (
                <div className="px-8 pb-8 pt-0 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="h-px bg-gray-100 w-full mb-6"></div>
                  <p className="text-charcoal-muted text-lg leading-relaxed font-medium">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Contact = ({ selectedPlan, setSelectedPlan }: { selectedPlan: string, setSelectedPlan: (s: string) => void }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    const formData = new FormData(e.currentTarget);
    const data = { 
      name: formData.get('name') as string, 
      media_name: formData.get('media') as string, 
      email: formData.get('email') as string, 
      phone: formData.get('phone') as string,
      plan_interest: selectedPlan,
      status: 'new',
      priority: 'media',
      notes: formData.get('message') as string,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    try {
      const { error: supabaseError } = await supabase.from('leads').insert([data]);
      if (supabaseError) throw supabaseError;
      setIsSuccess(true);
      
      // Reset form
      e.currentTarget.reset();
      setSelectedPlan('');
      
      // Log successful submission
      console.log('Lead submitted successfully:', data);
    } catch (err: any) {
      console.error('Error saving lead:', err);
      setError(err.message || 'Error al enviar la solicitud. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <section id="contacto" className="py-32 bg-white scroll-mt-24">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-24 items-center">
          <div>
            <h2 className="text-4xl lg:text-6xl font-extrabold text-charcoal mb-8 leading-tight">¿Listo para Acceder a Fondos?</h2>
            <p className="text-xl text-charcoal-muted mb-12 leading-relaxed">Agenda una consultoría gratuita de 30 minutos. Analizaremos el potencial de tu medio para captar fondos internacionales.</p>
            <div className="space-y-6">
              <div className="flex gap-4 items-center"><div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-primary"><CheckCircle2 size={24} /></div><p className="text-charcoal font-semibold">100% Confidencialidad Garantizada</p></div>
              <div className="flex gap-4 items-center"><div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-primary"><Globe size={24} /></div><p className="text-charcoal font-semibold">Expertos en GNI & Meta Journalism</p></div>
            </div>
          </div>
          <Card className="shadow-2xl border-none p-10">
            {isSuccess ? (
              <div className="text-center py-12 animate-in zoom-in duration-500">
                <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 size={48} />
                </div>
                <h3 className="text-2xl font-bold text-charcoal mb-2">¡Solicitud Enviada Exitosamente!</h3>
                <p className="text-charcoal-muted mb-4">
                  Gracias por tu interés en MediaNews Tech Lab. Tu solicitud ha sido recibida y está siendo procesada.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>Próximos pasos:</strong><br/>
                    • Revisaremos tu información detalladamente<br/>
                    • Te contactaremos en las próximas 24-48 horas<br/>
                    • Prepara tu consultoría gratuita de 30 minutos
                  </p>
                </div>
                <Button onClick={() => setIsSuccess(false)}>
                  Enviar otra consulta
                </Button>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm">
                    {error}
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-charcoal-muted ml-1">Nombre Completo</label>
                    <input name="name" required type="text" placeholder="Ej. Juan Pérez" className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-charcoal-muted ml-1">Nombre del Medio</label>
                    <input name="media" required type="text" placeholder="Ej. Diario Digital RD" className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-charcoal-muted ml-1">Email Corporativo</label>
                    <input name="email" required type="email" placeholder="juan@medio.com" className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-charcoal-muted ml-1">Teléfono (opcional)</label>
                    <input name="phone" type="tel" placeholder="+1 809-123-4567" className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-charcoal-muted ml-1">Plan de Interés</label>
                  <div className="relative">
                    <select value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium appearance-none cursor-pointer">
                      <option value="">Seleccione un plan (opcional)</option>
                      <option value="Medios Grandes">Medios Grandes</option>
                      <option value="Medios Medianos">Medios Medianos</option>
                      <option value="Medios Regionales">Medios Regionales</option>
                      <option value="Consultoría Express">Consultoría Express</option>
                    </select>
                    <ChevronDown size={18} className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-charcoal-muted ml-1">Mensaje (opcional)</label>
                  <textarea name="message" rows={4} placeholder="Cuéntanos sobre tus necesidades y objetivos..." className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium resize-none" />
                </div>
                <Button type="submit" disabled={isSubmitting} className="w-full py-5 text-lg">
                  {isSubmitting ? 'Enviando...' : 'Agendar Consultoría Gratis'} <ArrowRight />
                </Button>
              </form>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
};

const PageView = ({ slug, setView }: { slug: ViewType, setView: (v: ViewType) => void }) => {
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  const pageData: Record<string, { title: string, icon: React.ReactNode, content: React.ReactNode }> = {
    grants: { title: 'Gestión de Grants', icon: <Briefcase className="text-primary" size={48} />, content: (<div className="space-y-12"><p className="text-xl text-charcoal-muted leading-relaxed">Aseguramos que su medio digital no solo aplique, sino que gane.</p><div className="grid md:grid-cols-2 gap-8"><Card><h4 className="text-primary font-bold text-xl mb-4">Ciclo Estratégico</h4><ul className="space-y-4 text-charcoal-muted font-medium"><li className="flex gap-2"><CheckCircle2 size={18} className="text-primary shrink-0" /> Análisis de elegibilidad técnica</li><li className="flex gap-2"><CheckCircle2 size={18} className="text-primary shrink-0" /> Redacción de propuesta bilingüe</li></ul></Card><Card><h4 className="text-primary font-bold text-xl mb-4">Fondos Clave</h4><div className="flex flex-wrap gap-3">{['GNI', 'Meta Journalism', 'SIP Labs', 'Lenfest'].map(f => (<span key={f} className="px-4 py-2 bg-blue-50 rounded-full text-sm font-bold text-primary">{f}</span>))}</div></Card></div></div>) },
    seo: { title: 'Auditoría SEO News', icon: <Search className="text-primary" size={48} />, content: (<div className="space-y-12"><p className="text-xl text-charcoal-muted leading-relaxed">Optimizamos para Discover, Top Stories y Google News.</p><div className="space-y-6">{[{ label: 'Estructura de Datos', desc: 'Implementación correcta de NewsArticle schema.' }, { label: 'Core Web Vitals', desc: 'Optimización de velocidad y estabilidad visual.' }].map((item, i) => (<Card key={i} className="flex gap-6 items-center"><div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center font-bold text-primary shrink-0">{i+1}</div><div><p className="font-bold text-charcoal text-lg">{item.label}</p><p className="text-charcoal-muted font-medium">{item.desc}</p></div></Card>))}</div></div>) },
    ia: { title: 'Newsletters & IA', icon: <Rocket className="text-primary" size={48} />, content: (<div className="space-y-12"><p className="text-xl text-charcoal-muted leading-relaxed">Automatización inteligente para redacciones modernas.</p><div className="grid lg:grid-cols-3 gap-6">{['Resumen IA', 'Voice-to-Text', 'Analítica'].map((tool, i) => (<Card key={i} className="text-center"><Zap className="mx-auto text-primary mb-4" /><h5 className="font-bold text-charcoal">{tool}</h5></Card>))}</div></div>) },
    calendario: { title: 'Calendario de Grants 2026', icon: <Calendar className="text-primary" size={48} />, content: (<Card className="p-0 overflow-hidden"><table className="w-full text-left"><thead className="bg-gray-50 text-charcoal-muted uppercase text-xs font-bold tracking-widest"><tr><th className="px-8 py-4">Fondo / Grant</th><th className="px-8 py-4">Fecha Estimada</th></tr></thead><tbody className="text-charcoal font-medium"><tr className="border-t border-gray-100"><td className="px-8 py-6 font-bold">GNI Innovation Challenge</td><td className="px-8 py-6">Marzo 2026</td></tr><tr className="border-t border-gray-100"><td className="px-8 py-6 font-bold">Meta Engagement Grant</td><td className="px-8 py-6">Junio 2026</td></tr></tbody></table></Card>) },
    reportes: { title: 'Reportes de Industria', icon: <BarChart3 className="text-primary" size={48} />, content: (<Card className="text-center p-16"><h4 className="text-3xl font-bold text-charcoal mb-8">Panorama de Medios RD 2025</h4><Button className="mx-auto"><FileText size={18} /> Descargar Reporte (PDF)</Button></Card>) },
    terminos: { title: 'Términos de Servicio', icon: <ShieldCheck className="text-primary" size={48} />, content: (<div className="text-charcoal-muted space-y-6 font-medium"><p>Al utilizar los servicios de MediaNews Tech Lab, usted acepta quedar vinculado por los siguientes términos...</p><h4 className="text-charcoal font-bold text-xl mt-8">Confidencialidad</h4><p>Toda la información compartida será tratada bajo estrictos acuerdos de confidencialidad.</p></div>) }
  };
  const activePage = pageData[slug] || { title: 'En desarrollo', icon: <XCircle />, content: <p>Lo sentimos, esta sección está en desarrollo.</p> };
  return (
    <div className="min-h-screen bg-white pt-40 pb-24">
      <div className="container mx-auto px-6 max-w-4xl">
        <button onClick={() => { setView('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-2 text-primary font-bold mb-12 hover:translate-x-[-4px] transition-transform"><ArrowLeft size={20} /> Volver al Inicio</button>
        <header className="mb-16"><div className="mb-6">{activePage.icon}</div><h1 className="text-4xl lg:text-6xl font-extrabold text-charcoal mb-6">{activePage.title}</h1><div className="h-1.5 w-24 bg-primary rounded-full"></div></header>
        <main className="animate-in slide-in-from-bottom-4 duration-500">{activePage.content}</main>
      </div>
    </div>
  );
};

const Footer = ({ setView }: { setView: (v: ViewType) => void }) => (
  <footer className="bg-surface-variant pt-24 pb-12 border-t border-gray-200">
    <div className="container mx-auto px-6 max-w-7xl">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-20">
        <div className="space-y-6">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => { setView('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}><div className="w-8 h-8 bg-primary text-white rounded flex items-center justify-center font-bold">M</div><span className="font-bold text-lg text-charcoal">MediaNews <span className="text-primary">Lab</span></span></div>
          <p className="text-charcoal-muted text-sm leading-relaxed">Expertos en aceleración de medios digitales en el Caribe.</p>
        </div>
        <div><h4 className="font-bold text-charcoal mb-6 uppercase tracking-widest text-xs">Consultoría</h4><ul className="space-y-4 text-charcoal-muted text-sm font-medium"><li><button onClick={() => setView('grants')} className="hover:text-primary transition-colors">Gestión de Grants</button></li><li><button onClick={() => setView('seo')} className="hover:text-primary transition-colors">Auditoría SEO News</button></li><li><button onClick={() => setView('ia')} className="hover:text-primary transition-colors">Newsletters & IA</button></li></ul></div>
        <div><h4 className="font-bold text-charcoal mb-6 uppercase tracking-widest text-xs">Recursos</h4><ul className="space-y-4 text-charcoal-muted text-sm font-medium"><li><button onClick={() => setView('calendario')} className="hover:text-primary transition-colors">Calendario de Grants</button></li><li><button onClick={() => setView('reportes')} className="hover:text-primary transition-colors">Reportes de Industria</button></li></ul></div>
        <div><h4 className="font-bold text-charcoal mb-6 uppercase tracking-widest text-xs">Legal</h4><ul className="space-y-4 text-charcoal-muted text-sm font-medium"><li><button onClick={() => setView('terminos')} className="hover:text-primary transition-colors">Términos de Servicio</button></li></ul></div>
      </div>
      <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-gray-200 text-charcoal-muted text-xs font-medium"><p>© 2024 MediaNews Tech Lab. Todos los derechos reservados.</p></div>
    </div>
  </footer>
);

const LandingPage = ({ view, setView, selectedPlan, setSelectedPlan }: any) => {
  const handleSelectPlan = (plan: string) => { if (view !== 'home') setView('home'); setSelectedPlan(plan); setTimeout(() => { document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' }); }, 150); };
  const handleApplyNow = () => { if (view !== 'home') setView('home'); setSelectedPlan('Consultoría Express'); setTimeout(() => { document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' }); }, 150); };
  return (
    <div className="antialiased selection:bg-blue-100">
      <Header currentView={view} setView={setView} />
      {view === 'home' ? (<><Hero /><Marquee /><VideoCarousel /><ProblemSolution /><Services /><AvailableFunds onApplyNow={handleApplyNow} /><CaseStudies onAction={handleApplyNow} /><Process /><Pricing onSelectPlan={handleSelectPlan} /><FAQ /><Contact selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} /></>) : (<PageView slug={view} setView={setView} />)}
      <Footer setView={setView} />
    </div>
  );
};

export default function App() {
  const [view, setView] = useState<ViewType>('home');
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <Routes>
            <Route path="/" element={<LandingPage view={view} setView={setView} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="clients" element={<Clients />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="workshops" element={<Workshops />} />
              <Route path="resources" element={<Resources />} />
              <Route path="automations" element={<Automations />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}